'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Edit, Printer, Download, Copy, Trash2, CheckCircle, Clock, FileText, TrendingUp, TrendingDown, Minus, AlertTriangle, Egg, BarChart2, ClipboardList, User, Calendar, Home, Layers, ChevronRight, Info, Shield, MoreHorizontal, X } from 'lucide-react';
import {
  MOCK_EGG_PRODUCTION_RECORDS,
  EggProductionRecord,
  getProductionStatus,
} from '../../../components/eggProductionData';
import { useRole } from '@/lib/useRole';

// ─── Audit Trail ──────────────────────────────────────────────────────────────

interface AuditEntry {
  id: string;
  action: 'created' | 'edited' | 'approved' | 'submitted' | 'deleted' | 'duplicated';
  user: string;
  role: string;
  timestamp: string;
  changes?: { field: string; oldValue: string; newValue: string }[];
  notes?: string;
}

function buildAuditTrail(rec: EggProductionRecord): AuditEntry[] {
  const trail: AuditEntry[] = [
    {
      id: 'audit-1',
      action: 'created',
      user: rec.recordedBy,
      role: 'Farm Supervisor',
      timestamp: rec.createdAt,
      notes: 'Production record created from daily collection data.',
    },
  ];
  if (rec.status === 'submitted' || rec.status === 'approved') {
    trail.push({
      id: 'audit-2',
      action: 'submitted',
      user: rec.recordedBy,
      role: 'Farm Supervisor',
      timestamp: rec.updatedAt,
      notes: 'Record submitted for approval.',
    });
  }
  if (rec.status === 'approved') {
    trail.push({
      id: 'audit-3',
      action: 'approved',
      user: 'David Mugisha',
      role: 'Farm Manager',
      timestamp: rec.updatedAt,
      changes: [
        { field: 'Status', oldValue: 'Submitted', newValue: 'Approved' },
      ],
      notes: 'Record verified and approved.',
    });
  }
  return trail.reverse();
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: EggProductionRecord['status'] }) {
  const map = {
    approved: { cls: 'bg-green-100 text-green-700 border-green-200', icon: <CheckCircle size={12} />, label: 'Approved' },
    submitted: { cls: 'bg-blue-100 text-blue-700 border-blue-200', icon: <Clock size={12} />, label: 'Submitted' },
    draft: { cls: 'bg-gray-100 text-gray-600 border-gray-200', icon: <FileText size={12} />, label: 'Draft' },
  };
  const { cls, icon, label } = map[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${cls}`}>
      {icon}{label}
    </span>
  );
}

function PerformanceBadge({ status }: { status: ReturnType<typeof getProductionStatus> }) {
  const map = {
    excellent: { cls: 'bg-green-100 text-green-700', label: 'Excellent' },
    good: { cls: 'bg-blue-100 text-blue-700', label: 'Good' },
    below_target: { cls: 'bg-amber-100 text-amber-700', label: 'Below Target' },
    critical: { cls: 'bg-red-100 text-red-700', label: 'Critical' },
  };
  const { cls, label } = map[status];
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      {label}
    </span>
  );
}

function SummaryCard({
  label, value, sub, icon, color, trend,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-start gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className="text-xl font-bold text-gray-900 mt-0.5">{value}</p>
        {sub && (
          <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
            {trend === 'up' && <TrendingUp size={11} className="text-green-500" />}
            {trend === 'down' && <TrendingDown size={11} className="text-red-500" />}
            {trend === 'neutral' && <Minus size={11} className="text-gray-400" />}
            {sub}
          </p>
        )}
      </div>
    </div>
  );
}

function SectionCard({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
        <span className="text-green-600">{icon}</span>
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between py-2.5 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-500 font-medium w-40 flex-shrink-0">{label}</span>
      <span className={`text-xs text-gray-800 text-right flex-1 ${mono ? 'font-mono' : 'font-medium'}`}>{value}</span>
    </div>
  );
}

function ClassificationRow({
  label, value, total, color,
}: {
  label: string; value: number; total: number; color: string;
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="py-2.5 border-b border-gray-50 last:border-0">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-600 font-medium">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-gray-900">{value.toLocaleString()}</span>
          <span className="text-xs text-gray-400 w-10 text-right">{pct}%</span>
        </div>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function MetricRow({
  label, value, target, variance, unit = '%',
}: {
  label: string; value: number; target?: number; variance?: number; unit?: string;
}) {
  const isGood = variance === undefined ? true : variance >= 0;
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-500 font-medium">{label}</span>
      <div className="flex items-center gap-3">
        <span className="text-xs font-bold text-gray-900">{value}{unit}</span>
        {target !== undefined && (
          <span className="text-xs text-gray-400">/ {target}{unit}</span>
        )}
        {variance !== undefined && (
          <span className={`text-xs font-semibold flex items-center gap-0.5 ${isGood ? 'text-green-600' : 'text-red-500'}`}>
            {isGood ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {variance > 0 ? '+' : ''}{variance}{unit}
          </span>
        )}
      </div>
    </div>
  );
}

function AuditActionIcon({ action }: { action: AuditEntry['action'] }) {
  const map: Record<AuditEntry['action'], { icon: React.ReactNode; cls: string }> = {
    created: { icon: <FileText size={12} />, cls: 'bg-blue-100 text-blue-600' },
    edited: { icon: <Edit size={12} />, cls: 'bg-amber-100 text-amber-600' },
    approved: { icon: <CheckCircle size={12} />, cls: 'bg-green-100 text-green-600' },
    submitted: { icon: <Clock size={12} />, cls: 'bg-purple-100 text-purple-600' },
    deleted: { icon: <Trash2 size={12} />, cls: 'bg-red-100 text-red-600' },
    duplicated: { icon: <Copy size={12} />, cls: 'bg-gray-100 text-gray-600' },
  };
  const { icon, cls } = map[action];
  return (
    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${cls}`}>
      {icon}
    </div>
  );
}

function formatDateTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

function DeleteModal({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
            <Trash2 size={18} className="text-red-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Delete Production Record</h3>
            <p className="text-xs text-gray-500">This action cannot be undone.</p>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-5">
          Are you sure you want to delete this production record? All associated data including audit history will be permanently removed.
        </p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 transition-colors">
            Delete Record
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function EggRecordDetailContent({ id }: { id: string }) {
  const router = useRouter();
  const [showDelete, setShowDelete] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const { can } = useRole();

  const rec = MOCK_EGG_PRODUCTION_RECORDS.find(r => r.id === id);

  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  function handleDelete() {
    setShowDelete(false);
    showToast('Production record deleted successfully.');
    setTimeout(() => router.push('/egg-production/records'), 1500);
  }

  function handleDuplicate() {
    showToast('Record duplicated. Redirecting to new draft...');
    setTimeout(() => router.push('/egg-production/records/new'), 1500);
  }

  function handlePrint() {
    showToast('Opening print dialog...');
    setTimeout(() => window.print(), 500);
  }

  function handleExport() {
    showToast('Exporting record as PDF...');
  }

  if (!rec) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
          <FileText size={28} className="text-gray-400" />
        </div>
        <h2 className="text-lg font-bold text-gray-700">Record Not Found</h2>
        <p className="text-sm text-gray-400">The production record you are looking for does not exist or has been deleted.</p>
        <Link href="/egg-production/records" className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors">
          Back to Records
        </Link>
      </div>
    );
  }

  const perfStatus = getProductionStatus(rec.henDayProduction, rec.targetProduction ?? rec.henDayProduction);
  const auditTrail = buildAuditTrail(rec);

  const sizeTotal = rec.smallEggs + rec.mediumEggs + rec.largeEggs + rec.extraLargeEggs;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm ${toast.type === 'success' ? 'bg-gray-900' : 'bg-red-600'}`}>
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${toast.type === 'success' ? 'bg-green-400' : 'bg-white'}`} />
          {toast.msg}
        </div>
      )}

      {/* Delete Modal */}
      {showDelete && <DeleteModal onConfirm={handleDelete} onCancel={() => setShowDelete(false)} />}

      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-5">
        <Link href="/egg-production" className="hover:text-green-600 transition-colors">Egg Production</Link>
        <ChevronRight size={12} />
        <Link href="/egg-production/records" className="hover:text-green-600 transition-colors">Records</Link>
        <ChevronRight size={12} />
        <span className="text-gray-600 font-medium">{rec.recordNumber}</span>
      </nav>

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <Link
            href="/egg-production/records"
            className="mt-0.5 p-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors flex-shrink-0"
          >
            <ArrowLeft size={16} />
          </Link>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-gray-900">Production Record</h1>
              <StatusBadge status={rec.status} />
              <PerformanceBadge status={perfStatus} />
            </div>
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              <span className="text-sm font-semibold text-green-700">{rec.recordNumber}</span>
              <span className="text-gray-300">·</span>
              <span className="text-sm text-gray-500 flex items-center gap-1">
                <Calendar size={12} />{rec.productionDate}
              </span>
              <span className="text-gray-300">·</span>
              <span className="text-sm text-gray-500 flex items-center gap-1">
                <Home size={12} />{rec.house}
              </span>
              <span className="text-gray-300">·</span>
              <span className="text-sm text-gray-500 flex items-center gap-1">
                <Layers size={12} />{rec.flockNumber}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 text-xs font-medium rounded-xl hover:bg-gray-50 transition-colors"
          >
            <Printer size={14} />Print
          </button>
          {can('egg_production.export') && (
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 text-xs font-medium rounded-xl hover:bg-gray-50 transition-colors"
            >
              <Download size={14} />Export PDF
            </button>
          )}
          {(can('egg_production.create') || can('egg_production.delete')) && (
            <div className="relative">
              <button
                onClick={() => setShowMoreMenu(v => !v)}
                className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 text-xs font-medium rounded-xl hover:bg-gray-50 transition-colors"
              >
                <MoreHorizontal size={14} />More
              </button>
              {showMoreMenu && (
                <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl border border-gray-100 shadow-lg z-20 py-1 overflow-hidden">
                  {can('egg_production.create') && (
                    <button
                      onClick={() => { handleDuplicate(); setShowMoreMenu(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Copy size={13} />Duplicate Record
                    </button>
                  )}
                  {can('egg_production.delete') && (
                    <>
                      <div className="border-t border-gray-100 my-1" />
                      <button
                        onClick={() => { setShowDelete(true); setShowMoreMenu(false); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 size={13} />Delete Record
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {can('egg_production.edit') && (
            <Link
              href={`/egg-production/records/${rec.id}/edit`}
              className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-xs font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-sm"
            >
              <Edit size={14} />Edit Record
            </Link>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <SummaryCard
          label="Total Eggs"
          value={rec.totalEggs.toLocaleString()}
          sub={`${rec.morningCollection + rec.middayCollection + rec.eveningCollection} collected`}
          icon={<Egg size={18} className="text-amber-600" />}
          color="bg-amber-50"
        />
        <SummaryCard
          label="Good Eggs"
          value={rec.goodEggs.toLocaleString()}
          sub={`${rec.goodEggRate}% quality rate`}
          icon={<CheckCircle size={18} className="text-green-600" />}
          color="bg-green-50"
          trend="up"
        />
        <SummaryCard
          label="Rejected Eggs"
          value={rec.rejectedEggs.toLocaleString()}
          sub={`${rec.rejectionRate}% rejection rate`}
          icon={<X size={18} className="text-red-500" />}
          color="bg-red-50"
          trend={rec.rejectionRate > 3 ? 'down' : 'neutral'}
        />
        <SummaryCard
          label="Hen-Day %"
          value={`${rec.henDayProduction}%`}
          sub={rec.targetProduction ? `Target: ${rec.targetProduction}%` : undefined}
          icon={<BarChart2 size={18} className="text-blue-600" />}
          color="bg-blue-50"
          trend={rec.variance !== undefined ? (rec.variance >= 0 ? 'up' : 'down') : 'neutral'}
        />
        <SummaryCard
          label="Total Trays"
          value={`${rec.fullTrays} trays`}
          sub={`+ ${rec.looseEggs} loose eggs`}
          icon={<ClipboardList size={18} className="text-purple-600" />}
          color="bg-purple-50"
        />
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-5">

          {/* Production Information */}
          <SectionCard title="Production Information" icon={<Info size={15} />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
              <div>
                <InfoRow label="Record Number" value={<span className="font-mono text-green-700">{rec.recordNumber}</span>} />
                <InfoRow label="Production Date" value={rec.productionDate} />
                <InfoRow label="Farm" value={rec.farm} />
                <InfoRow label="Poultry House" value={rec.house} />
                <InfoRow label="Flock" value={`${rec.flockNumber} — ${rec.flockName}`} />
                <InfoRow label="Breed" value={rec.breed} />
              </div>
              <div>
                <InfoRow label="Bird Type" value={rec.birdType} />
                <InfoRow label="Age (Weeks)" value={`${rec.ageWeeks} weeks`} />
                <InfoRow label="Opening Population" value={rec.openingBirdPopulation.toLocaleString()} />
                <InfoRow label="Tray Size" value={`${rec.traySize} eggs/tray`} />
                <InfoRow label="Recorded By" value={rec.recordedBy} />
                <InfoRow label="Status" value={<StatusBadge status={rec.status} />} />
              </div>
            </div>

            {/* Collection Breakdown */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Egg Collection Breakdown</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Morning', value: rec.morningCollection, color: 'bg-amber-50 border-amber-100 text-amber-700' },
                  { label: 'Midday', value: rec.middayCollection, color: 'bg-orange-50 border-orange-100 text-orange-700' },
                  { label: 'Evening', value: rec.eveningCollection, color: 'bg-blue-50 border-blue-100 text-blue-700' },
                  { label: 'Other', value: rec.otherCollection, color: 'bg-gray-50 border-gray-100 text-gray-600' },
                ].map(c => (
                  <div key={c.label} className={`rounded-xl border p-3 ${c.color}`}>
                    <p className="text-xs font-medium opacity-70">{c.label}</p>
                    <p className="text-lg font-bold mt-0.5">{c.value.toLocaleString()}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between bg-gray-50 rounded-xl px-4 py-2.5">
                <span className="text-xs font-semibold text-gray-600">Total Eggs Collected</span>
                <span className="text-base font-bold text-gray-900">{rec.totalEggs.toLocaleString()}</span>
              </div>
            </div>
          </SectionCard>

          {/* Egg Classification */}
          <SectionCard title="Egg Classification" icon={<Egg size={15} />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">By Quality</p>
                <ClassificationRow label="Good Eggs" value={rec.goodEggs} total={rec.totalEggs} color="bg-green-500" />
                <ClassificationRow label="Cracked Eggs" value={rec.crackedEggs} total={rec.totalEggs} color="bg-amber-400" />
                <ClassificationRow label="Dirty Eggs" value={rec.dirtyEggs} total={rec.totalEggs} color="bg-orange-400" />
                <ClassificationRow label="Rejected Eggs" value={rec.rejectedEggs} total={rec.totalEggs} color="bg-red-500" />
                <ClassificationRow label="Hatching Eggs" value={rec.hatchingEggs} total={rec.totalEggs} color="bg-purple-400" />
                <ClassificationRow label="Double Yolk" value={rec.doubleYolkEggs} total={rec.totalEggs} color="bg-yellow-400" />
                <ClassificationRow label="Other" value={rec.otherEggs} total={rec.totalEggs} color="bg-gray-300" />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">By Size</p>
                <ClassificationRow label="Small Eggs" value={rec.smallEggs} total={sizeTotal || rec.totalEggs} color="bg-sky-300" />
                <ClassificationRow label="Medium Eggs" value={rec.mediumEggs} total={sizeTotal || rec.totalEggs} color="bg-sky-400" />
                <ClassificationRow label="Large Eggs" value={rec.largeEggs} total={sizeTotal || rec.totalEggs} color="bg-sky-500" />
                <ClassificationRow label="Extra Large" value={rec.extraLargeEggs} total={sizeTotal || rec.totalEggs} color="bg-sky-600" />
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Classified Total</span>
                  <span className="text-xs font-bold text-gray-900">{(rec.goodEggs + rec.crackedEggs + rec.dirtyEggs + rec.rejectedEggs + rec.hatchingEggs + rec.doubleYolkEggs + rec.otherEggs).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Tray Summary */}
          <SectionCard title="Tray Summary" icon={<ClipboardList size={15} />}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-center">
                <p className="text-xs text-green-600 font-medium">Tray Size</p>
                <p className="text-3xl font-bold text-green-700 mt-1">{rec.traySize}</p>
                <p className="text-xs text-green-500 mt-0.5">eggs per tray</p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-center">
                <p className="text-xs text-blue-600 font-medium">Full Trays</p>
                <p className="text-3xl font-bold text-blue-700 mt-1">{rec.fullTrays}</p>
                <p className="text-xs text-blue-500 mt-0.5">{(rec.fullTrays * rec.traySize).toLocaleString()} eggs</p>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-center">
                <p className="text-xs text-amber-600 font-medium">Loose Eggs</p>
                <p className="text-3xl font-bold text-amber-700 mt-1">{rec.looseEggs}</p>
                <p className="text-xs text-amber-500 mt-0.5">remaining</p>
              </div>
            </div>
            <div className="mt-4 bg-gray-50 rounded-xl px-4 py-3 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-600">Calculation</span>
              <span className="text-xs text-gray-500 font-mono">
                {rec.totalEggs.toLocaleString()} ÷ {rec.traySize} = {rec.fullTrays} trays + {rec.looseEggs} loose
              </span>
            </div>
          </SectionCard>

          {/* Performance Metrics */}
          <SectionCard title="Performance Metrics" icon={<BarChart2 size={15} />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Production Rates</p>
                <MetricRow
                  label="Hen-Day Production"
                  value={rec.henDayProduction}
                  target={rec.targetProduction}
                  variance={rec.variance}
                />
                <MetricRow label="Good Egg Rate" value={rec.goodEggRate} />
                <MetricRow label="Crack Rate" value={rec.crackRate} />
                <MetricRow label="Rejection Rate" value={rec.rejectionRate} />
                <MetricRow label="Dirty Egg Rate" value={rec.dirtyEggRate} />
                <MetricRow label="Hatching Egg Rate" value={rec.hatchingEggRate} />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Productivity</p>
                <MetricRow label="Eggs Per Bird" value={rec.eggsPerBird} unit="" />
                <MetricRow label="Bird Population" value={rec.openingBirdPopulation} unit="" />
                <MetricRow label="Age at Record" value={rec.ageWeeks} unit=" wks" />
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Performance Rating</p>
                  <div className="flex items-center gap-2">
                    <PerformanceBadge status={perfStatus} />
                    {rec.variance !== undefined && (
                      <span className={`text-xs font-medium ${rec.variance >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {rec.variance > 0 ? '+' : ''}{rec.variance}% vs target
                      </span>
                    )}
                  </div>
                  {perfStatus === 'below_target' || perfStatus === 'critical' ? (
                    <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                      <AlertTriangle size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-700">
                        Production is {Math.abs(rec.variance ?? 0)}% below the configured target of {rec.targetProduction}%. Review feed quality and flock health.
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </SectionCard>
        </div>

        {/* Right Column */}
        <div className="space-y-5">

          {/* Notes */}
          <SectionCard title="Notes & Observations" icon={<FileText size={15} />}>
            <div className="space-y-4">
              {rec.productionNotes ? (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Production Notes</p>
                  <p className="text-xs text-gray-700 leading-relaxed bg-gray-50 rounded-lg p-3">{rec.productionNotes}</p>
                </div>
              ) : null}
              {rec.observedProblems ? (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Observed Problems</p>
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-lg p-3">
                    <AlertTriangle size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700 leading-relaxed">{rec.observedProblems}</p>
                  </div>
                </div>
              ) : null}
              {rec.weatherNotes ? (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Weather / Environment</p>
                  <p className="text-xs text-gray-700 leading-relaxed bg-blue-50 rounded-lg p-3">{rec.weatherNotes}</p>
                </div>
              ) : null}
              {!rec.productionNotes && !rec.observedProblems && !rec.weatherNotes && (
                <p className="text-xs text-gray-400 italic">No notes recorded for this production entry.</p>
              )}
            </div>
          </SectionCard>

          {/* Record Metadata */}
          <SectionCard title="Record Details" icon={<User size={15} />}>
            <InfoRow label="Recorded By" value={rec.recordedBy} />
            <InfoRow label="Created At" value={formatDateTime(rec.createdAt)} />
            <InfoRow label="Last Updated" value={formatDateTime(rec.updatedAt)} />
            <InfoRow label="Record ID" value={<span className="font-mono text-xs text-gray-500">{rec.id}</span>} />
          </SectionCard>

          {/* Audit Trail */}
          <SectionCard title="Audit Trail" icon={<Shield size={15} />}>
            <div className="space-y-3">
              {auditTrail.map((entry, idx) => (
                <div key={entry.id} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <AuditActionIcon action={entry.action} />
                    {idx < auditTrail.length - 1 && (
                      <div className="w-px flex-1 bg-gray-100 mt-1 mb-0" style={{ minHeight: 16 }} />
                    )}
                  </div>
                  <div className="pb-3 flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs font-semibold text-gray-800 capitalize">{entry.action}</p>
                        <p className="text-xs text-gray-500">{entry.user} · <span className="text-gray-400">{entry.role}</span></p>
                      </div>
                      <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">{formatDateTime(entry.timestamp)}</span>
                    </div>
                    {entry.notes && (
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{entry.notes}</p>
                    )}
                    {entry.changes && entry.changes.length > 0 && (
                      <div className="mt-1.5 space-y-1">
                        {entry.changes.map((c, ci) => (
                          <div key={ci} className="flex items-center gap-1.5 text-xs">
                            <span className="text-gray-400">{c.field}:</span>
                            <span className="text-red-400 line-through">{c.oldValue}</span>
                            <span className="text-gray-400">→</span>
                            <span className="text-green-600 font-medium">{c.newValue}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Quick Actions</p>
            <div className="space-y-2">
              {can('egg_production.edit') && (
                <Link
                  href={`/egg-production/records/${rec.id}/edit`}
                  className="w-full flex items-center gap-2 px-3 py-2.5 bg-green-600 text-white text-xs font-semibold rounded-xl hover:bg-green-700 transition-colors"
                >
                  <Edit size={13} />Edit This Record
                </Link>
              )}
              {can('egg_production.create') && (
                <button
                  onClick={handleDuplicate}
                  className="w-full flex items-center gap-2 px-3 py-2.5 border border-gray-200 text-gray-600 text-xs font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <Copy size={13} />Duplicate Record
                </button>
              )}
              <button
                onClick={handlePrint}
                className="w-full flex items-center gap-2 px-3 py-2.5 border border-gray-200 text-gray-600 text-xs font-medium rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Printer size={13} />Print Record
              </button>
              {can('egg_production.export') && (
                <button
                  onClick={handleExport}
                  className="w-full flex items-center gap-2 px-3 py-2.5 border border-gray-200 text-gray-600 text-xs font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <Download size={13} />Export as PDF
                </button>
              )}
              {can('egg_production.delete') && (
                <button
                  onClick={() => setShowDelete(true)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 border border-red-100 text-red-600 text-xs font-medium rounded-xl hover:bg-red-50 transition-colors"
                >
                  <Trash2 size={13} />Delete Record
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
